import { useState, useCallback } from 'react';
import axiosInstance from '../services/axiosInstance';
import { API_ENDPOINTS } from '../services/api/apiConfig';

// Hook simple pour le cache des patients
export function useCachedPatientUpdateSimple(id: string | null) {
  const [patient, setPatient] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache simple avec localStorage
  const getCacheKey = useCallback((id: string | null) => id ? `patient_update_${id}` : '', []);

  const getCachedPatient = useCallback(() => {
    try {
      if (!id) return null;
      const cached = localStorage.getItem(getCacheKey(id));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [id, getCacheKey]);

  const setCachedPatient = useCallback((data: any) => {
    try {
      if (!data || !data.id) return;
      localStorage.setItem(getCacheKey(data.id), JSON.stringify(data));
      // Expirer après 10 minutes
      setTimeout(() => {
        localStorage.removeItem(getCacheKey(data.id));
      }, 10 * 60 * 1000);
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [getCacheKey]);

  const fetchPatient = useCallback(async () => {
    if (!id) return;

    // Vérifier le cache d'abord
    const cached = getCachedPatient();
    if (cached) {
      setPatient(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(
        API_ENDPOINTS.PATIENTS.GET_BY_ID(Number(id))
      );

      const patientData = response.data;
      setPatient(patientData);
      setCachedPatient(patientData); // Mettre en cache

    } catch (err) {
      setError("Erreur lors de la récupération du patient");
      console.error("Error fetching patient:", err);
    } finally {
      setLoading(false);
    }
  }, [id, getCachedPatient, setCachedPatient]);

  const refetch = useCallback(() => {
    // Invalider le cache et recharger
    const cacheKey = id ? getCacheKey(id) : null;
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
    }
    fetchPatient();
  }, [id, fetchPatient, getCacheKey]);

  return { patient, loading, error, refetch };
}
